import { html, nothing, type TemplateResult } from 'lit';
import { AiAdmin } from '@vitesse/proto/vitesse/ai/v1/ai_admin_pb.js';
import '@vitesse/base-ui/elements/vitesse-gated';
import { t, formatNumber } from '@vitesse/base-ui/i18n';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { fmtDateTime } from '@vitesse/base-ui/format';
import type { FindingView } from '@vitesse/base-ui/ai';
import { basisLabel, decisionText, decisionsFor, statusLabel, type Decision, type FindingNames } from './finding-labels';
import { cutReason, seconds } from '../../ai-tools/sandbox-card';

/*
 * ONE ENTRY, OPENED — everything the log keeps about it, in the order a person deciding on it reads:
 * what it says, what it asks for, what it rests on, what it is about, the runs behind it, and what
 * has been decided so far, by whom and why.
 *
 * A list row carries no code and no history (they would be most of a page), so the row opens on what
 * it has and the rest arrives from `FindingsManager.read`. References show their claim beside their
 * id: what a reader wants from "follows F-1" is usually that one sentence, and jumping to it would
 * lose the entry they are deciding on.
 */
export interface DetailContext {
  names: FindingNames;
  /** The entry read in full: undefined while it is read, null when it could not be. */
  full: FindingView | null | undefined;
  /** The claims of the entries it cites, by id, as they arrive. */
  refs: ReadonlyMap<string, string>;
  /** YYYY-MM-DD, to tell a follow-up that is due. */
  today: string;
  onDecide: (d: Decision) => void;
}

export function renderFindingDetail(row: FindingView, ctx: DetailContext): TemplateResult {
  const f = ctx.full ?? row;
  const ref = (id: string) => html`<span class="finding-ref"><b>${id}</b> ${ctx.refs.get(id) ?? ''}</span>`;
  const closed = f.status === 'done' || f.status === 'declined';
  const due = !!f.followUpOn && f.followUpOn <= ctx.today && !closed;
  const about = [...f.symbols, ...f.controllerIds.map((c) => ctx.names.controller(c))];
  return html`<div class="finding-detail">
    <dl>
      <dt>${t('findings.claim')}</dt>
      <dd class="finding-claim">${f.claim}</dd>
      ${
        f.action
          ? html`<dt>${t('findings.action')}</dt>
              <dd>${f.action} <span class="finding-muted">· ${t('findings.owner')}: ${f.owner || t('findings.ownerNone')}</span></dd>`
          : nothing
      }
      ${
        f.followUpOn
          ? html`<dt>${t('findings.followUp')}</dt>
              <dd class=${due ? 'finding-due' : ''}>${f.followUpOn}${due ? html` · ${t('findings.dueNow')}` : nothing}</dd>`
          : nothing
      }
      ${
        f.definitionId
          ? html`<dt>${t('findings.definition')}</dt>
              <dd>${ref(f.definitionId)}</dd>`
          : nothing
      }
      ${
        f.follows.length
          ? html`<dt>${t('findings.follows')}</dt>
              <dd>${f.follows.map(ref)}</dd>`
          : nothing
      }
      ${
        about.length
          ? html`<dt>${t('findings.about')}</dt>
              <dd>${about.join(' · ')}</dd>`
          : nothing
      }
      <dt>${t('findings.evidence')}</dt>
      <dd>
        ${f.evidence.length ? f.evidence.map((e, i) => renderRun(e, i, ctx.full)) : html`<span class="finding-muted">${t('findings.noEvidence')}</span>`}
      </dd>
      <dt>${t('findings.history')}</dt>
      <dd>${renderHistory(f, ctx)}</dd>
      <dt>${t('findings.basis')}</dt>
      <dd>${basisLabel(f.provenance, f.confidence)}</dd>
      <dt>${t('findings.recorded')}</dt>
      <dd class="finding-muted">${ctx.names.person(f.createdBy, f.createdByName)} · ${fmtDateTime(f.createdAtMs)}</dd>
    </dl>
    ${renderDecisions(f, ctx)}
  </div>`;
}

/**
 * The opened row's height, from what it holds — the grid needs a number per row (record-grid.ts),
 * and one number for every entry either clips the decisions under a long history or leaves a short
 * baseline in a tall empty box. Lines are estimated, then clamped; anything beyond scrolls inside.
 */
export function detailHeight(f: FindingView): number {
  const runs = f.evidence.reduce((n, e) => n + 2 + e.datasets.length * 2 + (e.files.length ? 1 : 0), 0);
  const lines =
    6 + // claim, about, basis, recorded, history's first line, room
    (f.action ? 1 : 0) +
    (f.followUpOn ? 1 : 0) +
    (f.definitionId ? 1 : 0) +
    f.follows.length +
    Math.max(1, runs) +
    f.history.reduce((n, h) => n + (h.reason ? 2 : 1), 0);
  const actions = decisionsFor(f.status).length ? 48 : 0;
  return Math.min(560, Math.max(200, lines * 22 + actions + 32));
}

function renderRun(e: FindingView['evidence'][number], i: number, full: FindingView | null | undefined): TemplateResult {
  return html`<div class="finding-run">
    <div><b>${e.label || t('findings.run', { n: i + 1 })}</b> <span class="finding-muted">· ${seconds(e.ms)}</span></div>
    ${e.datasets.map(
      // The same words the run's own card uses for the same dataset: its rows, counted in the reader's
      // number format, and why it was cut.
      (d) =>
        html`<div class="finding-muted">
          ${d.name} · ${d.rows < 0 ? t('findings.rowsUnknown') : t('ai.pyRows', { count: d.rows, rows: formatNumber(d.rows) })} ·
          ${d.source}${d.truncatedBy ? html` · ${cutReason(d.truncatedBy)}` : nothing}
        </div>`,
    )}
    ${e.files.length ? html`<div class="finding-muted">${t('findings.produced')}: ${e.files.map((x) => x.filename).join(', ')}</div>` : nothing}
    ${
      full && e.code
        ? html`<details>
            <summary>${t('findings.code')}</summary>
            <pre>${e.code}</pre>
          </details>`
        : nothing
    }
  </div>`;
}

function renderHistory(f: FindingView, ctx: DetailContext): TemplateResult {
  if (ctx.full === undefined) return html`<span class="finding-muted">${t('findings.reading')}</span>`;
  if (ctx.full === null) return html`<span class="finding-muted">${t('findings.readError')}</span>`;
  if (!f.history.length) return html`<span class="finding-muted">${t('findings.nothingToDecide')}</span>`;
  return html`${f.history.map((h) => {
    const who = ctx.names.person(h.byUser, h.byUserName);
    return html`<div class="finding-event">
      <b>${h.status === 'proposed' ? t('findings.proposed') : statusLabel(h.status)}</b>
      <span class="finding-muted">· ${h.byRun ? t('findings.byAgentFor', { who }) : who} · ${fmtDateTime(h.atMs)}</span>
      ${h.reason ? html`<div class="finding-reason">“${h.reason}”</div>` : nothing}
    </div>`;
  })}`;
}

function renderDecisions(f: FindingView, ctx: DetailContext): unknown {
  const can = decisionsFor(f.status);
  if (!can.length) return nothing;
  // Gated on the RPC's own annotation: without the permission a lock says why, rather than the
  // buttons vanishing and the reader wondering how anything here is ever decided.
  return html`<div class="finding-actions">
    <vitesse-gated .rpc=${AiAdmin.method.setFindingStatus} inline>
      ${can.map((d) =>
        button({ label: decisionText(d, f.id).button, color: d === 'declined' ? 'red' : 'blue', onClick: () => ctx.onDecide(d) }),
      )}
    </vitesse-gated>
  </div>`;
}
